/**
 * Broadcast channel utilities for synchronizing timecard tracking state across browser tabs
 */

export type TimecardBroadcastMessage =
    | {
          type: 'TRACKING_STARTED';
          payload: {
              timecardId: string;
              timestamp: string | null;
              totalTimeInSeconds: number;
              stopwatchRunningSince: string | null;
              senderId: string;
          };
      }
    | {
          type: 'TRACKING_PAUSED';
          payload: { timecardId: string; totalTimeInSeconds: number; senderId: string };
      }
    | {
          type: 'TRACKING_RESUMED';
          payload: {
              timecardId: string;
              timestamp: string | null;
              totalTimeInSeconds: number;
              stopwatchRunningSince: string | null;
              senderId: string;
          };
      }
    | { type: 'TRACKING_FINISHED'; payload: { timecardId: string; senderId: string } }
    | { type: 'STATE_SYNC_REQUEST'; payload: { senderId: string } }
    | {
          type: 'STATE_SYNC_RESPONSE';
          payload: { timecardId: string | null; isRunning: boolean; senderId: string; requesterId: string };
      };

/**
 * Generates a unique ID for this tab/window to prevent processing own messages
 */
export const generateTabId = (): string => {
    return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Creates a BroadcastChannel instance scoped to a specific user and tenant
 */
export const createTimecardBroadcastChannel = (userId: string, tenantId: string): BroadcastChannel => {
    return new BroadcastChannel(`timecard-tracking-${userId}-${tenantId}`);
};

/**
 * Checks if BroadcastChannel API is supported
 */
export const isBroadcastChannelSupported = (): boolean => {
    return typeof window !== 'undefined' && 'BroadcastChannel' in window;
};

/**
 * Type guard to check if a message is a valid TimecardBroadcastMessage
 */
export const isTimecardBroadcastMessage = (data: unknown): data is TimecardBroadcastMessage => {
    if (!data || typeof data !== 'object') return false;
    const message = data as TimecardBroadcastMessage;
    return (
        message.type &&
        [
            'TRACKING_STARTED',
            'TRACKING_PAUSED',
            'TRACKING_RESUMED',
            'TRACKING_FINISHED',
            'STATE_SYNC_REQUEST',
            'STATE_SYNC_RESPONSE',
        ].includes(message.type) &&
        message.payload &&
        typeof message.payload === 'object'
    );
};
